import React, { useState, useEffect, useRef } from 'react'
import {
  Zap,
  Code,
  Bug,
  Copy,
  ChevronDown,
  Sparkles,
  Clock,
  Layers,
  Share,
  X,
  XCircle,
  Star,
  FileText,
  Check,
  Radio,
  BookOpen,
  FileCode,
  Settings
} from 'lucide-react'
import { AIService } from '../services/aiService'
import { AIServiceEnhanced } from '../services/aiServiceEnhanced'
import { checkBackendHealth } from '../utils/healthCheck'
import { useZenSettings } from '../hooks/useZenSettings'
import { useProviders } from '../hooks/useProviders'
import { useAdminMode } from '../hooks/useAdminMode'
import ProviderSelector from './chat/ProviderSelector'
import ZenSimpleDebugPanel, { ApiCall } from './ZenSimpleDebugPanel'
import { SyntaxLensWidget } from './SyntaxLensWidget'
import { codeSamples } from '../data/codeSamples'
import { defaultLanguages, sourceLanguages, targetLanguages } from '../config/languages'
import { shareService, ShareData } from '../services/shareService'
import { generateShareUrl } from '../utils/shareUrl'
import NotebookView from './NotebookView'
import InlineCommentsView from './InlineCommentsView'
import { validateCodeLanguage, ValidationResult } from '../utils/languageValidator'
import LanguageMismatchModal from './LanguageMismatchModal'
import LanguageSelector from './LanguageSelector'
import PulsatingOrb from './PulsatingOrb'
import CodeMirrorEditor from './CodeMirrorEditor'
import AdminSettingsPanel from './AdminSettingsPanel'

interface ZenCreatorSimpleProps {
  className?: string
}

const ZenCreatorSimple: React.FC<ZenCreatorSimpleProps> = ({ className = '' }) => {
  // Load saved language selection from localStorage
  const loadSavedLanguages = () => {
    try {
      const saved = localStorage.getItem('syntaxlens-language-selection')
      if (saved) {
        const parsed = JSON.parse(saved)
        if (parsed.source && parsed.target) {
          // Validate that languages still exist in the configuration
          const sourceExists = sourceLanguages.some(lang => lang.code === parsed.source)
          const targetExists = targetLanguages.some(lang => lang.code === parsed.target)

          if (sourceExists && targetExists) {
            return { source: parsed.source, target: parsed.target }
          } else {
            console.warn('Saved languages no longer valid, using defaults')
          }
        }
      }
    } catch (error) {
      console.warn('Failed to load saved language selection:', error)
    }
    return { source: defaultLanguages.source, target: defaultLanguages.target }
  }

  const savedLanguages = loadSavedLanguages()
  const [sourceLanguage, setSourceLanguage] = useState(savedLanguages.source)
  const [targetLanguage, setTargetLanguage] = useState(savedLanguages.target)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [showOutput, setShowOutput] = useState(false)
  const [activeTab, setActiveTab] = useState('lens')
  const [leftPanelWidth, setLeftPanelWidth] = useState(25)
  const [isDragging, setIsDragging] = useState(false)
  const [code, setCode] = useState('')
  const [generatedHTML, setGeneratedHTML] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [inlineComments, setInlineComments] = useState<string | null>(null)
  const [isCommentsGenerating, setIsCommentsGenerating] = useState(false)
  const [aiService, setAIService] = useState<AIService | null>(null)
  const [aiServiceEnhanced, setAIServiceEnhanced] = useState<AIServiceEnhanced | null>(null)
  const [isDebugOpen, setIsDebugOpen] = useState(false)
  const [apiCalls, setApiCalls] = useState<ApiCall[]>([])
  const [promptVersion, setPromptVersion] = useState('v1')
  const [isPromptSelectorOpen, setIsPromptSelectorOpen] = useState(false)
  const [isSampleSelectorOpen, setIsSampleSelectorOpen] = useState(false)
  const [selectedSample, setSelectedSample] = useState('fibonacci')
  const [sharing, setSharing] = useState({ loading: false, url: '', error: '' })
  const [copied, setCopied] = useState(false)
  const [showHoverHint, setShowHoverHint] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [diffAnalysis, setDiffAnalysis] = useState<string | null>(null)
  const [isDiffGenerating, setIsDiffGenerating] = useState(false)
  const [showDiffTab, setShowDiffTab] = useState(false)
  const showNotebookTab = true // Always enabled
  const [languageMismatch, setLanguageMismatch] = useState<ValidationResult | null>(null)
  const [showMismatchModal, setShowMismatchModal] = useState(false)
  const [detectionResult, setDetectionResult] = useState<ValidationResult | null>(null)
  const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false)

  const {
    getPreferredProvider,
    autoDetectLanguage,
    enableLanguagePairPrompts,
    enablePromptCaching,
    updateSetting
  } = useZenSettings()

  // DEBUG: Log language-pair settings on mount and when they change
  useEffect(() => {
    console.log('🔧 [ZenCreator DEBUG] Language-pair settings:', {
      enableLanguagePairPrompts,
      enablePromptCaching,
      timestamp: new Date().toISOString()
    })
  }, [enableLanguagePairPrompts, enablePromptCaching])
  const { providers, currentProvider, switchProvider, isLoadingProviders } = useProviders()
  const isAdmin = useAdminMode()
  const [speedMode, setSpeedMode] = useState<'quality' | 'fast'>('fast')
  const [isLanguageHighlighted, setIsLanguageHighlighted] = useState(true)

  // Streaming toggle state (persisted in localStorage, admin-only feature)
  const [streamingEnabled, setStreamingEnabled] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('syntaxlens-streaming-enabled')
      return saved ? JSON.parse(saved) : false // Default to disabled
    } catch {
      return false
    }
  })

  // Track initial mount to avoid loading sample code on first render
  const isInitialMount = useRef(true)

  // Get current sample for selected language
  const getCurrentSample = () => {
    const sample = codeSamples[selectedSample as keyof typeof codeSamples]
    const langKey = targetLanguage as keyof typeof sample.code
    return sample.code[langKey] || sample.code.JavaScript
  }

  // Get first available sample for target language (or empty if none exists)
  const getFirstAvailableSample = () => {
    // Get the first sample key
    const firstSampleKey = Object.keys(codeSamples)[0]
    const firstSample = codeSamples[firstSampleKey as keyof typeof codeSamples]
    const langKey = targetLanguage as keyof typeof firstSample.code

    // Return the code if it exists for this language, otherwise empty string
    return firstSample.code[langKey] || ''
  }

  // Initialize backend services
  useEffect(() => {
    const initService = async () => {
      const available = await checkBackendHealth()
      if (available) {
        setAIService(new AIService())
        setAIServiceEnhanced(new AIServiceEnhanced())
      } else {
        setError('Backend server not available. Please start it with: npm run start:backend')
      }
    }
    initService()

    // Check URL parameter for debug flag
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('debug') === 'true') {
      setIsDebugOpen(true)
    }
  }, [])

  // Auto-load first sample when language tuple changes (not when manually selecting samples)
  useEffect(() => {
    const firstAvailableSample = getFirstAvailableSample()
    setCode(firstAvailableSample)

    // Reset to first sample when language changes
    setSelectedSample(Object.keys(codeSamples)[0])
  }, [sourceLanguage, targetLanguage])

  // Load sample code when user manually selects a sample
  useEffect(() => {
    // Skip on initial mount (code is already loaded by language change effect)
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Load code for any selected sample (including first sample on re-selection)
    setCode(getCurrentSample())
  }, [selectedSample])
  
  // Auto-select default provider if none is currently selected
  useEffect(() => {
    if (!currentProvider && providers.length > 0) {
      const preferredProvider = getPreferredProvider(providers)
      if (preferredProvider) {
        switchProvider(preferredProvider)
      }
    }
  }, [currentProvider, providers, getPreferredProvider, switchProvider])

  // Load experimental features settings from localStorage and listen for changes
  useEffect(() => {
    const loadExperimentalSettings = () => {
      const savedSettings = localStorage.getItem('zenCreatorSettings')
      if (savedSettings) {
        try {
          const parsed = JSON.parse(savedSettings)
          setShowDiffTab(parsed.diffAnalysisEnabled === true)
          // Notebook view is always enabled, no need to read from settings
        } catch (error) {
          console.warn('Failed to parse settings for experimental features:', error)
        }
      }
    }

    // Load initial value
    loadExperimentalSettings()

    // Listen for storage events (when settings are saved)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'zenCreatorSettings') {
        loadExperimentalSettings()
      }
    }

    window.addEventListener('storage', handleStorageChange)

    // Also listen for custom event (for same-tab changes)
    const handleSettingsChange = () => {
      loadExperimentalSettings()
    }
    window.addEventListener('zenSettingsChanged', handleSettingsChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('zenSettingsChanged', handleSettingsChange)
    }
  }, [])

  // Attach synchronized hover event listeners to diff analysis elements
  useEffect(() => {
    if (!diffAnalysis) return

    // Small delay to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      const hoverableElements = document.querySelectorAll('.hoverable')

      const handleMouseEnter = (e: Event) => {
        const target = e.currentTarget as HTMLElement
        const pairId = target.dataset.pairId
        if (pairId) {
          // Highlight all elements with the same pair ID
          document.querySelectorAll(`[data-pair-id="${pairId}"]`).forEach((el) => {
            (el as HTMLElement).style.backgroundColor = 'rgba(255, 200, 0, 0.4)'
            ;(el as HTMLElement).style.outline = '2px solid rgba(255, 200, 0, 0.6)'
          })
        }
      }

      const handleMouseLeave = (e: Event) => {
        const target = e.currentTarget as HTMLElement
        const pairId = target.dataset.pairId
        if (pairId) {
          // Remove highlight from all elements with the same pair ID
          document.querySelectorAll(`[data-pair-id="${pairId}"]`).forEach((el) => {
            (el as HTMLElement).style.backgroundColor = ''
            ;(el as HTMLElement).style.outline = ''
          })
        }
      }

      hoverableElements.forEach((el) => {
        el.addEventListener('mouseenter', handleMouseEnter)
        el.addEventListener('mouseleave', handleMouseLeave)
      })

      // Cleanup function to remove event listeners
      return () => {
        hoverableElements.forEach((el) => {
          el.removeEventListener('mouseenter', handleMouseEnter)
          el.removeEventListener('mouseleave', handleMouseLeave)
        })
      }
    }, 100)

    return () => clearTimeout(timer)
  }, [diffAnalysis])

  // Handle streaming toggle
  const handleStreamingToggle = (enabled: boolean) => {
    setStreamingEnabled(enabled)
    try {
      localStorage.setItem('syntaxlens-streaming-enabled', JSON.stringify(enabled))
      console.log(`🌊 Streaming ${enabled ? 'enabled' : 'disabled'}`)
    } catch (error) {
      console.warn('Failed to save streaming preference:', error)
    }
  }

  // Helper to find the best provider for a given mode
  const getProviderForMode = (mode: 'quality' | 'fast') => {
    return providers.find(p => {
      if (mode === 'quality') {
        // Look for Claude Haiku 4.5
        return p.provider === 'anthropic' && p.model.includes('claude-haiku-4-5')
      } else {
        // Look for Cerebras models via direct Cerebras provider
        // Priority determined by backend modelRegistry order (Qwen models listed first)
        return p.provider === 'cerebras' && (
          p.model.includes('qwen-3-235b-a22b-instruct') || // Accept flagship Qwen
          p.model.includes('qwen-3-32b') || // Accept smaller Qwen
          p.model.includes('llama-3.3-70b') || // Accept Llama fallback
          p.model.includes('llama3.1-8b') || // Accept smaller Llama
          p.model.includes('gpt-oss-120b') // Accept GPT fallback
        )
      }
    })
  }

  // Handle speed mode toggle
  const handleSpeedModeToggle = (mode: 'quality' | 'fast') => {
    setSpeedMode(mode)

    // Find and switch to the appropriate provider
    const targetProvider = getProviderForMode(mode)

    if (targetProvider) {
      switchProvider(targetProvider)
    }
  }

  // Show toast notification
  const showToastNotification = (message: string) => {
    setToastMessage(message)
    setShowToast(true)
    setTimeout(() => setShowToast(false), 3000)
  }

  // Handle language pair change from LanguageSelector
  const handleLanguageChange = (source: string, target: string) => {
    setSourceLanguage(source)
    setTargetLanguage(target)

    // Clear all generated content when language pair changes
    setGeneratedHTML(null)
    setShowOutput(false)
    setDiffAnalysis(null)
    setError(null)
    setSharing({ loading: false, url: '', error: '' })
    setLanguageMismatch(null)
    setShowMismatchModal(false)
  }

  // Generate diff analysis
  const handleGenerateDiff = async () => {
    if (!aiServiceEnhanced || !code.trim()) {
      setError('Please generate lens insights first')
      return
    }

    setIsDiffGenerating(true)
    setError(null)

    try {
      const selectedProvider = currentProvider || getPreferredProvider(providers)

      const diffResult = await aiServiceEnhanced.generateDiffAnalysis(
        code,
        sourceLanguage,
        targetLanguage,
        selectedProvider
      )

      setDiffAnalysis(diffResult.html)
      setActiveTab('diff')
    } catch (err) {
      console.error('Diff generation error:', err)
      setError(err instanceof Error ? err.message : 'Failed to generate diff analysis')
    } finally {
      setIsDiffGenerating(false)
    }
  }

  const handleGenerateComments = async () => {
    if (!aiService || !code.trim()) {
      setError('Please enter code to analyze')
      return
    }

    setIsCommentsGenerating(true)
    setError(null)

    try {
      const selectedProvider = currentProvider || getPreferredProvider(providers)

      const codeWithComments = await aiService.generateLensComments(
        code,
        sourceLanguage,
        targetLanguage,
        selectedProvider
      )

      setInlineComments(codeWithComments)
      setActiveTab('comments')
    } catch (err) {
      console.error('Comments generation error:', err)
      setError(err instanceof Error ? err.message : 'Failed to generate inline comments')
    } finally {
      setIsCommentsGenerating(false)
    }
  }

  const handleGenerate = async () => {
    // Route to appropriate handler based on active tab
    if (activeTab === 'comments') {
      await handleGenerateComments()
      return
    }

    if (activeTab === 'diff') {
      await handleGenerateDiff()
      return
    }

    // Initial validation checks
    if (!aiService || !aiServiceEnhanced) {
      setError('Backend service not initialized')
      return
    }

    if (!code.trim()) {
      setError('Please enter some code to analyze')
      return
    }

    // Language consistency validation (only if enabled in settings)
    if (autoDetectLanguage) {
      const validation = validateCodeLanguage(code, targetLanguage)

      // Store detection result for inline display
      setDetectionResult(validation)

      // If exact mismatch detected, show modal
      if (validation.isMismatch) {
        setLanguageMismatch(validation)
        setShowMismatchModal(true)
        return // Wait for user decision
      }
    } else {
      // Clear detection result if feature is disabled
      setDetectionResult(null)
    }

    // If match or feature disabled - proceed
    await performGeneration()
  }

  // Extracted generation logic to be reusable after validation
  const performGeneration = async () => {
    setIsGenerating(true)
    setError(null)
    setGeneratedHTML(null)

    // Determine which provider to use based on admin mode
    let selectedProvider

    if (isAdmin) {
      // Admin mode: use the provider from dropdown
      selectedProvider = currentProvider || getPreferredProvider(providers)
    } else {
      // Non-admin mode: use Fast/Quality toggle
      const targetProvider = getProviderForMode(speedMode)
      selectedProvider = targetProvider || currentProvider || getPreferredProvider(providers)
    }

    // Validate provider is available before proceeding
    if (!selectedProvider) {
      setError('AI provider not available. Please wait a moment and try again.')
      setIsGenerating(false)
      return
    }

    if (!aiServiceEnhanced) {
      setError('AI Service not initialized')
      setIsGenerating(false)
      return
    }

    // Create API call tracking object
    const callId = `call-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
    const startTime = Date.now()
    const apiCall: ApiCall = {
      id: callId,
      timestamp: new Date(),
      request: {
        type: 'lens-html',
        sourceLanguage,
        targetLanguage,
        code,
        provider: selectedProvider,
        url: import.meta.env.VITE_API_BASE_URL || '',  // Use Vite proxy in dev
        headers: {
          'Content-Type': 'application/json'
        }
      },
      status: 'pending'
    }

    // Add to tracking
    setApiCalls(prev => [...prev, apiCall])

    try {
      // Streaming logic based on selected model:
      // - Quality models (Claude Haiku 4.5): Always stream (progressive rendering of detailed output)
      // - Fast models (Cerebras): Never stream (responses are 200-800ms, faster without streaming)
      // - Other models: Respect admin streaming toggle
      const isQualityModel = selectedProvider?.model.includes('claude-haiku-4-5')
      const isFastModel = selectedProvider?.provider === 'cerebras' ||
                          selectedProvider?.model.includes('qwen-3-') || // All Qwen 3 models are ultra-fast
                          selectedProvider?.model.includes('llama-3.3-70b') ||
                          selectedProvider?.model.includes('llama3.1-8b') ||
                          selectedProvider?.model.includes('gpt-oss-120b')

      let shouldStream: boolean
      if (isFastModel) {
        // Fast models: Never stream (complete response is near-instant)
        shouldStream = false
      } else if (isQualityModel) {
        // Quality models: Always stream (for progressive UX with longer generation)
        shouldStream = true
      } else {
        // Other models: Respect admin toggle
        shouldStream = isAdmin && streamingEnabled
      }

      const useStreaming = shouldStream && aiServiceEnhanced?.generateLensHTMLStream

      if (useStreaming) {
        setShowOutput(true) // Show output panel immediately
        setIsStreaming(true) // Disable hover during streaming

        let accumulatedHTML = ''
        let finalUsage: any = null
        let finalAiProviderDetails: any = null
        let chunkCount = 0
        let streamError: Error | null = null

        try {
          if (!aiServiceEnhanced?.generateLensHTMLStream) {
            throw new Error('generateLensHTMLStream method not available')
          }

          const streamGenerator = aiServiceEnhanced.generateLensHTMLStream(
            code,
            sourceLanguage,
            targetLanguage,
            undefined,
            selectedProvider,
            promptVersion,
            // Always pass language-pair and caching settings (not just in admin mode)
            { enableLanguagePairPrompts, enablePromptCaching }
          )

          for await (const event of streamGenerator) {
            if (event.type === 'chunk') {
              chunkCount++
              accumulatedHTML = event.totalContent || (accumulatedHTML + (event.content || ''))

              // Update the display progressively (only update every few chunks to reduce re-renders)
              if (chunkCount % 5 === 0 || accumulatedHTML.length > 100) {
                setGeneratedHTML(accumulatedHTML)
              }

            } else if (event.type === 'done') {
              finalUsage = event.usage
              finalAiProviderDetails = event.aiProviderDetails

            } else if (event.type === 'error') {
              console.error('❌ Stream error:', event.error)
              const errorMsg = event.error || 'Streaming error occurred'
              setError(errorMsg)
              streamError = new Error(errorMsg)
              break

            } else if (event.type === 'complete') {
              break
            }
          }

        } catch (err) {
          console.error('❌ Stream consumption error:', err)
          const errorMsg = `Streaming failed: ${err instanceof Error ? err.message : 'Unknown error'}`
          setError(errorMsg)
          streamError = err instanceof Error ? err : new Error(errorMsg)

          // Fall back to accumulated HTML if we got any
          if (accumulatedHTML.length > 0) {
            setGeneratedHTML(accumulatedHTML)
          }
        } finally {
          // Re-enable hover after streaming (success or fail)
          setIsStreaming(false)
        }

        const endTime = Date.now()

        // Clean up HTML if needed (remove markdown code blocks if present)
        let cleanedHTML = accumulatedHTML
        const htmlMatch = cleanedHTML.match(/^```html\s*([\s\S]*?)\s*```$/m)
        if (htmlMatch) {
          cleanedHTML = htmlMatch[1].trim()
          console.log('🧹 Removed markdown code block wrapper')
        }

        // Ensure final HTML is set (even if last chunks didn't trigger update)
        if (cleanedHTML !== generatedHTML) {
          console.log('🎨 Setting final HTML:', cleanedHTML.length, 'chars')
          setGeneratedHTML(cleanedHTML)
        }

        // Update API call tracking
        setApiCalls(prev => prev.map(call =>
          call.id === callId
            ? {
                ...call,
                response: {
                  status: streamError ? 500 : 200,
                  statusText: streamError ? 'Stream Error' : 'OK',
                  data: cleanedHTML,
                  headers: {},
                  metadata: {
                    responseTime: endTime - startTime,
                    model: selectedProvider?.model,
                    provider: selectedProvider?.provider,
                    streaming: true
                  },
                  usage: finalUsage,
                  aiProviderDetails: finalAiProviderDetails,
                  error: streamError ? streamError.message : undefined
                },
                duration: endTime - startTime,
                status: streamError ? 'error' : 'success'
              }
            : call
        ))

        // Trigger hover hint animation only if successful
        if (!streamError) {
          setShowHoverHint(true)
        }

      } else {
        // Use non-streaming generation
        const enhancedResult = await aiServiceEnhanced.generateLensHTMLWithDetails(
          code,
          sourceLanguage, // The language we know (insights will be in this context)
          targetLanguage, // The code language (what we're analyzing)
          undefined, // Let backend determine concept from code
          selectedProvider, // Pass the selected provider with model
          promptVersion, // Pass the selected prompt version
          // Always pass language-pair and caching settings (not just in admin mode)
          { enableLanguagePairPrompts, enablePromptCaching }
        )

        const endTime = Date.now()

        // Update API call with success and complete response details from enhanced service
        setApiCalls(prev => prev.map(call =>
          call.id === callId
            ? {
                ...call,
                response: {
                  status: enhancedResult.status,
                  statusText: enhancedResult.statusText,
                  data: enhancedResult.html,
                  headers: enhancedResult.responseHeaders,
                  metadata: {
                    title: enhancedResult.title,
                    description: enhancedResult.description,
                    responseTime: endTime - startTime,
                    model: selectedProvider?.model,
                    provider: selectedProvider?.provider
                  },
                  rawResponse: enhancedResult.rawResponse || enhancedResult,
                  aiProviderDetails: enhancedResult.aiProviderDetails,
                  usage: enhancedResult.usage
                },
                duration: endTime - startTime,
                status: 'success' as const
              }
            : call
        ))

        // Remove HTML wrapper tags if present
        let cleanedHTML = enhancedResult.html
        const htmlMatch = cleanedHTML.match(/^```html\s*([\s\S]*?)\s*```$/m)
        if (htmlMatch) {
          cleanedHTML = htmlMatch[1].trim()
        }

        setGeneratedHTML(cleanedHTML)
        setShowOutput(true)

        // Trigger hover hint animation
        setShowHoverHint(true)
      }
    } catch (err) {
      const endTime = Date.now()
      console.error('❌❌❌ OUTER CATCH: Generation error:', err)
      console.error('❌❌❌ Error type:', err?.constructor?.name)
      console.error('❌❌❌ Error message:', err instanceof Error ? err.message : String(err))
      console.error('❌❌❌ Error stack:', err instanceof Error ? err.stack : 'No stack')
      
      // Update API call with error details
      setApiCalls(prev => prev.map(call =>
        call.id === callId
          ? {
              ...call,
              response: {
                status: err instanceof Error && 'status' in err ? (err as any).status : 500,
                statusText: err instanceof Error && 'statusText' in err ? (err as any).statusText : 'Internal Server Error',
                data: '',
                error: err instanceof Error ? err.message : 'Failed to generate lens insights',
                headers: err instanceof Error && 'headers' in err ? (err as any).headers : {}
              },
              duration: endTime - startTime,
              status: 'error' as const
            }
          : call
      ))
      
      setError(err instanceof Error ? err.message : 'Failed to generate lens insights')
    } finally {
      setIsGenerating(false)
    }
  }

  // Language mismatch modal handler - just close and let user fix selection
  const handleCancelGeneration = () => {
    setShowMismatchModal(false)
    setLanguageMismatch(null)
    // User will update language selection in top-right selector
  }

  const handleCopyHTML = async () => {
    if (!generatedHTML) {
      return
    }

    try {
      // Get the latest API call (most recent generation)
      const latestCall = apiCalls[apiCalls.length - 1]

      // Debug: Log what we have
      console.log('📋 Copy Debug:', {
        hasLatestCall: !!latestCall,
        hasResponse: !!latestCall?.response,
        hasAiProviderDetails: !!latestCall?.response?.aiProviderDetails,
        latestCall: latestCall
      })

      // Build structured output with prompt + response
      let outputText = ''

      if (latestCall && latestCall.response?.aiProviderDetails) {
        const details = latestCall.response.aiProviderDetails
        const metadata = latestCall.response.metadata

        // Extract prompt from request body
        let promptText = ''
        if (details.request.body?.messages) {
          // Messages format (with caching)
          promptText = JSON.stringify(details.request.body.messages, null, 2)
        } else if (details.request.body?.prompt) {
          // String format (legacy)
          promptText = details.request.body.prompt
        } else {
          // Fallback: show entire request body
          promptText = JSON.stringify(details.request.body, null, 2)
        }

        // Build XML-structured output for easy copy-paste into ChatGPT
        outputText = `<syntaxlens-request>
  <metadata>
    <timestamp>${new Date(latestCall.timestamp).toISOString()}</timestamp>
    <languages>${latestCall.request.sourceLanguage} → ${latestCall.request.targetLanguage}</languages>
    <provider>${details.provider}</provider>
    <model>${metadata?.model || 'unknown'}</model>
    <strategy>${metadata?.strategy || 'unknown'}</strategy>
    <promptTemplate>${metadata?.promptTemplate || 'unknown'}</promptTemplate>
    <responseTime>${metadata?.responseTime || 0}ms</responseTime>
    <tokens>
      <input>${latestCall.response.usage?.inputTokens || 0}</input>
      <output>${latestCall.response.usage?.outputTokens || 0}</output>
      <total>${latestCall.response.usage?.totalTokens || 0}</total>
    </tokens>
  </metadata>

  <prompt>
${promptText}
  </prompt>

  <response>
${generatedHTML}
  </response>
</syntaxlens-request>`
      } else {
        // Fallback: just copy HTML if no API details available
        outputText = generatedHTML
      }

      await navigator.clipboard.writeText(outputText)
      showToastNotification('📋 Copied prompt + response to clipboard!')
      console.log('✅ Copied structured output (prompt + HTML) to clipboard')
    } catch (err) {
      console.error('Failed to copy:', err)
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = generatedHTML
      document.body.appendChild(textArea)
      textArea.select()
      try {
        document.execCommand('copy')
        showToastNotification('📋 Copied to clipboard (fallback)')
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr)
        showToastNotification('❌ Failed to copy')
      }
      document.body.removeChild(textArea)
    }
  }

  // Share functionality
  const handleShare = async () => {
    if (sharing.loading || !generatedHTML) return

    setSharing({ loading: true, url: '', error: '' })

    try {
      // Collect generation metadata for this share
      const generationMetadata = {
        provider: currentProvider?.name || 'unknown',
        model: currentProvider?.model || 'unknown',
        promptVersion: promptVersion,
        isStreaming: streamingEnabled,
        quality: speedMode,
        enableLanguagePairPrompts: enableLanguagePairPrompts,
        generatedAt: new Date().toISOString()
      }

      // Create share with essential data + generation metadata
      const shareData: ShareData = {
        code,
        sourceLanguage,
        targetLanguage,
        htmlContent: generatedHTML,
        generationMetadata
      }

      console.log('📤 Creating share with metadata:', generationMetadata)

      const response = await shareService.createShare(shareData)

      // Generate semantic URL with language context (e.g., /snippet/ctojs-0fcb7df0)
      const cleanShareUrl = generateShareUrl(
        response.id,
        sourceLanguage,
        targetLanguage,
        window.location.origin
      )

      // Try to copy to clipboard, but don't fail if it doesn't work
      try {
        await shareService.copyToClipboard(cleanShareUrl)
        console.log('✅ Share URL copied to clipboard')
      } catch (clipboardError) {
        console.warn('Clipboard copy failed, but share was created successfully:', clipboardError)
        // Still show success with URL - user can copy manually
      }

      setSharing({
        loading: false,
        url: cleanShareUrl,
        error: ''
      })

    } catch (error) {
      console.error('Share failed:', error)
      setSharing({
        loading: false,
        url: '',
        error: error instanceof Error ? error.message : 'Failed to create share'
      })
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    e.preventDefault()
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return
    
    const container = document.querySelector('.split-container')
    if (!container) return
    
    const rect = container.getBoundingClientRect()
    const newWidth = ((e.clientX - rect.left) / rect.width) * 100
    
    // Constrain between 15% and 60%
    const constrainedWidth = Math.min(Math.max(newWidth, 15), 60)
    setLeftPanelWidth(constrainedWidth)
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  // Add event listeners for mouse move and up
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }
  }, [isDragging])

  // Close sample selector when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      if (isSampleSelectorOpen && !target.closest('.sample-selector-container')) {
        setIsSampleSelectorOpen(false)
      }
    }

    if (isSampleSelectorOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isSampleSelectorOpen])

  // Animate hover hint from orange to gray after generation
  React.useEffect(() => {
    if (showHoverHint) {
      const timer = setTimeout(() => {
        setShowHoverHint(false)
      }, 3000) // Hold orange for 3 seconds, then fade to gray
      return () => clearTimeout(timer)
    }
  }, [showHoverHint])

  // Fade language selector colors after 30 seconds
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsLanguageHighlighted(false)
    }, 20000) // 30 seconds
    return () => clearTimeout(timer)
  }, [])

  return (
    <div
      id="slens-creator-root"
      data-component="zen-creator-simple"
      className={`zen-creator-simple h-screen bg-warm-bg overflow-hidden ${className}`}
    >
      {/* TOP HEADER BAR - Contains logo, language selector, speed toggle, provider selector */}
      <header
        id="slens-header"
        data-section="header"
        className="bg-warm-bg border-b border-gray-200 shadow-sm"
      >
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left Side - Navigation & Title */}
            <div className="flex items-center gap-6">
              <button className="flex items-center gap-3 hover:opacity-80 transition-opacity duration-200">
              <svg className="w-10 h-10" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 28 H16 Q14 28 14 30 V70 Q14 72 16 72 H20" stroke="black" strokeWidth="5" fill="none" strokeLinecap="square"/>
                <path d="M80 28 H84 Q86 28 86 30 V70 Q86 72 84 72 H80" stroke="black" strokeWidth="5" fill="none" strokeLinecap="square"/>
                <polygon points="50,32 69,51 50,70 31,51" fill="none" stroke="black" strokeWidth="5.5"/>
                <circle cx="50" cy="50" r="5.2" fill="black"/>
              </svg>
              <div>
                <div className="text-left">
                <h1 className="text-xl font-bold text-gray-900">SyntaxLens</h1>
                <p className="text-xs text-gray-500 -mt-0.5">Lens Studio</p>
                </div>
              </div>
              </button>
            </div>

            {/* Right Side - Controls */}
            <div className="flex items-center gap-4">
              {/* Speed Mode Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  data-testid="speed-button-fast"
                  data-state={speedMode === 'fast' ? 'active' : 'inactive'}
                  aria-label="Switch to fast generation mode using Cerebras Qwen 3 235B (ultra-fast inference)"
                  onClick={() => handleSpeedModeToggle('fast')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                    speedMode === 'fast'
                      ? 'bg-white text-orange-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Zap className={`w-3.5 h-3.5 ${speedMode === 'fast' ? 'text-orange-500' : ''}`} />
                  <span>Fast</span>
                </button>
                <button
                  data-testid="speed-button-quality"
                  data-state={speedMode === 'quality' ? 'active' : 'inactive'}
                  aria-label="Switch to quality generation mode using Claude Haiku 4.5"
                  onClick={() => handleSpeedModeToggle('quality')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                    speedMode === 'quality'
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Star className={`w-3.5 h-3.5 ${speedMode === 'quality' ? 'text-blue-500' : ''}`} />
                  <span>Quality</span>
                </button>
              </div>

              {/* Compact Language Context */}
              <LanguageSelector
                sourceLanguage={sourceLanguage}
                targetLanguage={targetLanguage}
                onLanguageChange={handleLanguageChange}
                onShowToast={showToastNotification}
                isHighlighted={isLanguageHighlighted}
              />

              {/* Admin Settings Button - Admin Only */}
              {isAdmin && (
                <button
                  data-testid="admin-settings-button"
                  onClick={() => setIsAdminPanelOpen(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg hover:from-orange-100 hover:to-amber-100 transition-all duration-200 shadow-sm"
                  aria-label="Open admin settings panel"
                  title="Admin Settings (Performance, Prompts, Provider)"
                >
                  <Settings className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium text-orange-700">Admin</span>
                </button>
              )}

            </div>
          </div>
        </div>
      </header>

      {/* MAIN SPLIT CONTAINER - Contains left code pane + divider + right output pane */}
      <div
        id="slens-split-container"
        data-section="split-container"
        className="flex h-[calc(100vh-80px)] split-container"
      >
        {/* LEFT CODE INPUT PANE - Code editor with sample selector and generate button */}
        <div
          id="slens-left-pane"
          data-section="code-input-pane"
          className="bg-warm-bg border-r border-gray-200 flex flex-col overflow-hidden"
          style={{ width: `${leftPanelWidth}%` }}
        >
          {/* Code Editor - Full Height */}
          <div className="flex-1 flex flex-col p-4 overflow-hidden">
            <div className="mb-3 flex items-center justify-between">
              {/* Left: label + sample selector */}
              <div className="flex items-center gap-3">
                <label className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
                  <Code className="w-3 h-3 text-blue-500" />
                  {targetLanguage}
                </label>

                {/* Elegant Sample Selector */}
                <div className="relative sample-selector-container">
                  <button
                    onClick={() => setIsSampleSelectorOpen(!isSampleSelectorOpen)}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-orange-600 transition-colors duration-200 bg-gray-50 hover:bg-orange-50 px-2 py-1 rounded-md border border-gray-200 hover:border-orange-200"
                    title="Code Examples"
                  >
                    <Layers className="w-3 h-3" />
                    <ChevronDown className="w-3 h-3" />
                  </button>

                  {/* Zen Sample Dropdown */}
                  {isSampleSelectorOpen && (
                    <div className="absolute top-full left-0 mt-2 w-72 bg-warm-bg rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
                      <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-100">
                        <h4 className="text-sm font-medium text-gray-800 flex items-center gap-2">
                          <Code className="w-4 h-4 text-orange-500" />
                          Examples
                        </h4>
                      </div>

                      <div className="p-2 space-y-1 max-h-80 overflow-y-auto">
                        {(() => {
                          const availableSamples = Object.entries(codeSamples).filter(([_, sample]) => {
                            // Only show samples that have code for the target language
                            const langKey = targetLanguage as keyof typeof sample.code
                            return sample.code[langKey] !== undefined
                          })

                          if (availableSamples.length === 0) {
                            return (
                              <div className="p-4 text-center">
                                <p className="text-sm text-gray-500">
                                  Examples not available for {targetLanguage}
                                </p>
                              </div>
                            )
                          }

                          return availableSamples.map(([key, sample]) => (
                            <button
                              key={key}
                              onClick={() => {
                                setSelectedSample(key);
                                setIsSampleSelectorOpen(false);
                              }}
                              className={`w-full group relative overflow-hidden rounded-lg transition-all duration-200 ${
                                selectedSample === key
                                  ? 'bg-orange-50 shadow-sm ring-1 ring-orange-200'
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center gap-3 p-3">
                                {/* Content */}
                                <div className="flex-1 text-left">
                                  <div className="flex items-center gap-2 mb-0.5">
                                    <h5 className="font-medium text-gray-900 text-sm leading-tight">
                                      {sample.name}
                                    </h5>
                                    <div
                                      className={`w-2 h-2 rounded-full ${
                                        sample.difficulty === 'Beginner'
                                          ? 'bg-green-400'
                                          : 'bg-blue-400'
                                      }`}
                                    />
                                  </div>
                                  <p className="text-xs text-gray-500 leading-relaxed">
                                    {sample.description}
                                  </p>
                                </div>

                                {/* Selection indicator */}
                                {selectedSample === key && (
                                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center">
                                    <div className="w-2 h-2 bg-white rounded-full" />
                                  </div>
                                )}
                              </div>
                            </button>
                          ))
                        })()}
                      </div>
                    </div>
                  )}
                </div>
                {/* /Elegant Sample Selector */}
              </div>
              
              {/* Right: Generate Button */}
              <button
                data-testid="generator-button-generate"
                data-state={(isGenerating || isCommentsGenerating || isDiffGenerating) ? 'generating' : 'idle'}
                aria-label="Generate AI-powered syntax insights for entered code"
                onClick={handleGenerate}
                disabled={isGenerating || isCommentsGenerating || isDiffGenerating || !code.trim() || !aiService || !aiServiceEnhanced || isLoadingProviders}
                className={`px-3 py-2 rounded-lg transition-all duration-200 flex items-center justify-center gap-1.5 shadow-sm text-sm font-medium ${
                  isGenerating || isCommentsGenerating || isDiffGenerating
                    ? 'bg-orange-50 cursor-default text-orange-700 border border-orange-300'
                    : !code.trim() || !aiService || !aiServiceEnhanced || isLoadingProviders
                    ? 'bg-gray-400 cursor-not-allowed text-white'
                    : 'bg-orange-100 hover:bg-orange-200 text-orange-700 border border-orange-300 hover:border-orange-400'
                }`}
                title={
                  isLoadingProviders
                    ? 'Loading AI providers...'
                    : (isGenerating || isCommentsGenerating || isDiffGenerating)
                    ? 'Analyzing...'
                    : activeTab === 'comments'
                      ? 'Generate Inline Comments'
                      : activeTab === 'diff'
                        ? 'Generate Diff Analysis'
                        : 'Generate AI Insights'
                }
              >
                {(isGenerating || isCommentsGenerating || isDiffGenerating) ? (
                  <>
                    <PulsatingOrb size="sm" variant="orange" />
                    <span>Analyzing...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    <span>Generate</span>
                  </>
                )}
              </button>
            </div>

            {/* Error Display */}
            {error && (
              <div
                data-testid="error-message"
                className="mb-3 bg-red-50 border border-red-300 rounded-lg p-3"
              >
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            {/* Enhanced Code Editor - Clean & Uniform */}
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 bg-gray-900 rounded-xl overflow-hidden min-h-0">
                {/* Code Content - CodeMirror Editor with Syntax Highlighting */}
                <div className="h-full" data-testid="code-editor">
                  <CodeMirrorEditor
                    value={code}
                    onChange={setCode}
                    language={targetLanguage}
                    placeholder={`Paste your ${targetLanguage} code here...`}
                    height="100%"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RESIZABLE DIVIDER - Drag to adjust left/right pane widths */}
        <div
          id="slens-divider"
          data-section="resizable-divider"
          className={`w-1 cursor-col-resize transition-all duration-200 relative group flex-shrink-0 ${
            isDragging
            ? 'bg-orange-500'
            : 'bg-gray-300 hover:bg-gray-400'
        }`}
          onMouseDown={handleMouseDown}
          style={{ pointerEvents: 'auto' }}
        >
          {/* Drag Handle Visual Indicator - Nearly Invisible */}
          <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1 h-8 rounded-full transition-all duration-200 ${
            isDragging
              ? 'bg-orange-400 opacity-100'
              : 'bg-slate-400 opacity-0 group-hover:opacity-60'
          }`}>
            <div className={`w-full h-0.5 rounded-full mt-1 transition-all duration-200 ${
              isDragging ? 'bg-white' : 'bg-slate-500'
            }`}></div>
            <div className={`w-full h-0.5 rounded-full mt-1.5 transition-all duration-200 ${
              isDragging ? 'bg-white' : 'bg-slate-500'
            }`}></div>
            <div className={`w-full h-0.5 rounded-full mt-1.5 transition-all duration-200 ${
              isDragging ? 'bg-white' : 'bg-slate-500'
            }`}></div>
          </div>
        </div>

        {/* RIGHT OUTPUT PANE - Shows generated lens, diff, or notebook view with grid pattern background */}
        <div
          id="slens-right-pane"
          data-section="output-pane"
          className="bg-warm-bg flex flex-col overflow-hidden relative"
          style={{ width: `${100 - leftPanelWidth}%` }}
        >
          {showOutput ? (
            <>
              {/* TAB NAVIGATION BAR - Switches between Lens, Diff, and Notebook views */}
              <div
                id="slens-tab-bar"
                data-section="tab-navigation"
                className="bg-warm-bg border-b border-gray-200"
              >
                <div className="flex items-center justify-between px-6 py-2">
                    {/* Tab Navigation */}
                    <div className="flex space-x-6" role="tablist" aria-label="View selection">
                      {[
                        { key: 'lens', label: 'Interactive Lens', desc: 'Interactive hover insights' },
                        ...(showNotebookTab ? [{ key: 'notebook', label: 'Notebook', desc: 'Notebook-style sequential breakdown' }] : []),
                        ...(isAdmin ? [{ key: 'comments', label: 'Inline Comments', desc: 'Code with inline comment insights', experimental: true }] : []),
                        ...(isAdmin && showDiffTab ? [{ key: 'diff', label: 'Syntax Diff', desc: 'Compare syntax and semantics between languages', experimental: true }] : []),
                      ].map((tab) => (
                        <button
                          key={tab.key}
                          id={`slens-tab-${tab.key}`}
                          role="tab"
                          data-testid={`tab-${tab.key}`}
                          data-state={activeTab === tab.key ? 'active' : 'inactive'}
                          aria-selected={activeTab === tab.key}
                          aria-controls={`slens-panel-${tab.key}`}
                          aria-label={`View ${tab.label} - ${tab.desc}`}
                          onClick={() => setActiveTab(tab.key)}
                          className={`py-2 text-sm font-medium border-b-2 transition-colors group flex items-center gap-2 ${
                            activeTab === tab.key
                              ? 'border-gray-400 text-gray-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700'
                          }`}
                          title={tab.desc}
                        >
                          {tab.label}
                          {'experimental' in tab && tab.experimental && (
                            <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">Beta</span>
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Action Buttons - Prominent Share */}
                    <div className="flex items-center gap-2">
                      {/* Prominent Share Button */}
                      <button
                        data-testid="share-button"
                        data-state={sharing.loading ? 'loading' : generatedHTML ? 'ready' : 'disabled'}
                        aria-label="Share generated insights"
                        onClick={handleShare}
                        disabled={sharing.loading || !generatedHTML}
                        className={`flex items-center gap-2 px-4 py-1.5 rounded-lg font-medium transition-all border-2 ${
                          sharing.loading
                            ? 'border-orange-400 text-orange-700 bg-orange-50 cursor-wait'
                            : generatedHTML
                            ? 'border-orange-500 text-orange-600 hover:bg-orange-50'
                            : 'border-gray-300 text-gray-400 cursor-not-allowed'
                        }`}
                        title={sharing.loading ? "Creating share link..." : !generatedHTML ? "Generate content first" : "Share this translation"}
                      >
                        {sharing.loading ? (
                          <>
                            <PulsatingOrb size="sm" variant="orange" />
                            <span className="text-sm">Sharing...</span>
                          </>
                        ) : (
                          <>
                            <Share className="w-4 h-4" />
                            <span className="text-sm">Share</span>
                          </>
                        )}
                      </button>

                      {/* Secondary: Copy Prompt + Response - Admin Only */}
                      {isAdmin && (
                        <button
                          onClick={handleCopyHTML}
                          disabled={!generatedHTML}
                          className={`p-1.5 rounded transition-colors ${
                            generatedHTML
                              ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                              : 'text-gray-300 cursor-not-allowed'
                          }`}
                          title={generatedHTML ? "Copy Prompt + Response (XML format for ChatGPT review)" : "No content to copy"}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                </div>
              </div>

              {/* TAB CONTENT AREA - Displays active tab content (lens/diff/notebook) */}
              <div
                id="slens-tab-content"
                data-section="tab-content"
                className="flex-1 overflow-auto"
              >
                {activeTab === 'lens' ? (
                  <div
                    id="slens-panel-lens"
                    role="tabpanel"
                    data-testid="panel-lens"
                    aria-labelledby="slens-tab-lens"
                  >
                  <SyntaxLensWidget
                    className="h-full"
                    sourceLanguage={sourceLanguage}
                    targetLanguage={targetLanguage}
                    isStreaming={isStreaming}
                    code={code}
                    generatedHTML={generatedHTML || ''}
                    onHTMLUpdate={setGeneratedHTML}
                    provider={currentProvider}
                  >
                    <div className="p-8 bg-gray-900">
                      <div className="mb-4 text-center">
                        <p
                          className={`text-sm font-medium mb-2 transition-colors duration-1000 ${
                            showHoverHint ? 'text-orange-400' : 'text-gray-400'
                          }`}
                        >
                          ✨ Hover over any highlighted element below
                        </p>
                        <p className="text-gray-400 text-xs">Navigate: ← → <span className="text-gray-500">•</span> Depth: 1 2 3 <span className="text-gray-500">•</span> Pin: Space <span className="text-gray-500">•</span> Exit: Esc</p>
                      </div>
                      <div className="font-mono text-sm space-y-2 text-left">
                        {generatedHTML ? (
                          <div dangerouslySetInnerHTML={{ __html: generatedHTML }} />
                        ) : isGenerating ? (
                          <div className="text-gray-500 py-4 flex items-start gap-3">
                            <PulsatingOrb size="md" variant="orange" />
                            <p className="text-sm mt-1">Generating interactive lens insights...</p>
                          </div>
                        ) : (
                          <div className="text-gray-500 text-center py-8">
                            Click "Generate Lens Insights" to see interactive analysis
                          </div>
                        )}
                      </div>
                    </div>
                  </SyntaxLensWidget>
                  </div>
                ) : activeTab === 'diff' ? (
                  <div
                    id="slens-panel-diff"
                    role="tabpanel"
                    data-testid="panel-diff"
                    aria-labelledby="slens-tab-diff"
                  >
                  <div className="h-full bg-gradient-to-br from-warm-bg to-warm-bg-dark p-8">
                    {diffAnalysis ? (
                      <div className="max-w-6xl mx-auto">
                        <div className="mb-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <span className="text-2xl">🔀</span>
                            Syntax & Semantic Comparison
                          </h3>
                          <p className="text-gray-600 text-sm">
                            Analyzing differences between <span className="font-semibold text-blue-600">{sourceLanguage}</span> and <span className="font-semibold text-orange-600">{targetLanguage}</span>
                          </p>
                        </div>
                        <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: diffAnalysis }} />
                      </div>
                    ) : isDiffGenerating ? (
                      <div className="pt-8 px-8">
                        <div className="flex items-start gap-3 text-gray-600">
                          <PulsatingOrb size="md" variant="orange" />
                          <p className="text-sm mt-1">Analyzing differences between {sourceLanguage} and {targetLanguage}...</p>
                        </div>
                      </div>
                    ) : (
                      <div className="h-full flex items-center justify-center">
                        <div className="text-center max-w-md">
                          <div className="mb-4">
                            <span className="text-6xl">🔀</span>
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">Compare Syntax & Semantics</h3>
                          <p className="text-gray-600 mb-6">
                            Generate an AI-powered comparison showing how code constructs differ between {sourceLanguage} and {targetLanguage}.
                          </p>
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                            <p className="text-sm text-purple-800">
                              <span className="font-semibold">🧪 Experimental Feature:</span> This analysis identifies syntax variations, semantic equivalences, and paradigm shifts between languages.
                            </p>
                          </div>
                          <button
                            onClick={handleGenerate}
                            disabled={isDiffGenerating || !code.trim()}
                            className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 mx-auto ${
                              code.trim() && !isDiffGenerating
                                ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg hover:shadow-xl'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            {isDiffGenerating ? (
                              <>
                                <PulsatingOrb size="md" />
                                <span>Analyzing Differences...</span>
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-5 h-5" />
                                <span>Generate Diff Analysis</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  </div>
                ) : activeTab === 'comments' ? (
                  <div
                    id="slens-panel-comments"
                    role="tabpanel"
                    data-testid="panel-comments"
                    aria-labelledby="slens-tab-comments"
                  >
                  <div className="h-full bg-gradient-to-br from-warm-bg to-warm-bg-dark p-8">
                    {inlineComments ? (
                      <InlineCommentsView
                        code={inlineComments}
                        targetLanguage={targetLanguage}
                      />
                    ) : isCommentsGenerating ? (
                      <div className="pt-8 px-8">
                        <div className="flex items-start gap-3 text-gray-600">
                          <PulsatingOrb size="md" variant="orange" />
                          <p className="text-sm mt-1">Generating inline comments with layered insights...</p>
                        </div>
                      </div>
                    ) : (
                      <div className="h-full flex items-center justify-center">
                        <div className="text-center max-w-md">
                          <div className="mb-4">
                            <span className="text-6xl">💬</span>
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">Inline Comment Insights</h3>
                          <p className="text-gray-600 mb-6">
                            Generate {targetLanguage} code with inline comment blocks that explain how {sourceLanguage} concepts map to {targetLanguage}.
                          </p>
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                            <p className="text-sm text-amber-800">
                              <span className="font-semibold">📝 Format:</span> Code with INSIGHT comment blocks (Surface → Concept → Paradigm layers) placed inline before relevant lines.
                            </p>
                          </div>
                          <button
                            onClick={handleGenerate}
                            disabled={isCommentsGenerating || !code.trim()}
                            className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 mx-auto ${
                              code.trim() && !isCommentsGenerating
                                ? 'bg-amber-600 hover:bg-amber-700 text-white shadow-lg hover:shadow-xl'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            {isCommentsGenerating ? (
                              <>
                                <PulsatingOrb size="md" />
                                <span>Generating Comments...</span>
                              </>
                            ) : (
                              <>
                                <FileText className="w-5 h-5" />
                                <span>Generate Inline Comments</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  </div>
                ) : activeTab === 'notebook' ? (
                  <div
                    id="slens-panel-notebook"
                    role="tabpanel"
                    data-testid="panel-notebook"
                    aria-labelledby="slens-tab-notebook"
                  >
                  <NotebookView
                    html={generatedHTML || ''}
                    sourceLanguage={sourceLanguage}
                    targetLanguage={targetLanguage}
                    code={code}
                    provider={currentProvider}
                    onHTMLUpdate={setGeneratedHTML}
                  />
                  </div>
                ) : null}

                {/* Stats Footer - Bottom Right */}
                {generatedHTML && (
                  <div className="absolute bottom-0 right-0 bg-gray-900/80 backdrop-blur-sm px-3 py-1.5 rounded-tl-lg border-t border-l border-gray-700">
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span className="flex items-center gap-1" title="Generation time">
                        <Clock className="w-3 h-3" />
                        {apiCalls.length > 0 && apiCalls[apiCalls.length - 1].duration
                          ? `${(apiCalls[apiCalls.length - 1].duration! / 1000).toFixed(1)}s`
                          : '0s'}
                      </span>
                      <span className="flex items-center gap-1" title="Content size">
                        <Zap className="w-3 h-3" />
                        {(generatedHTML.length / 1000).toFixed(1)}K
                      </span>
                      <span className="flex items-center gap-1" title="API calls">
                        <Bug className="w-3 h-3" />
                        {apiCalls.length}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* EMPTY STATE PLACEHOLDER - Shows before any content is generated */
            <div
              id="slens-placeholder"
              data-section="empty-state"
              className="flex-1 flex items-center justify-center bg-warm-bg"
            >
              <div className="text-center p-12 max-w-lg">
                {/* SyntaxLens Logo */}
                <div className="mx-auto mb-6">
                  <svg className="w-24 h-24 mx-auto" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 28 H16 Q14 28 14 30 V70 Q14 72 16 72 H20" stroke="black" strokeWidth="5" fill="none" strokeLinecap="square"/>
                    <path d="M80 28 H84 Q86 28 86 30 V70 Q86 72 84 72 H80" stroke="black" strokeWidth="5" fill="none" strokeLinecap="square"/>
                    <polygon points="50,32 69,51 50,70 31,51" fill="none" stroke="black" strokeWidth="5.5"/>
                    <circle cx="50" cy="50" r="5.2" fill="black"/>
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Start Exploring Code
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6 text-base">
                  Paste your {targetLanguage} code and hit <span className="inline-flex items-center px-2 py-0.5 rounded bg-orange-100 text-orange-700 text-sm font-medium mx-1"><Sparkles className="w-3 h-3 mr-1" />Generate</span> to see interactive AI insights
                </p>
                <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                    <span>Instant Insights</span>
                  </div>
                  <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                    <span>AI-powered</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div
          data-testid="toast"
          className="fixed top-4 right-4 z-50 animate-in slide-in-from-top duration-200"
        >
          <div className="bg-warm-bg rounded-lg shadow-xl border border-gray-200 px-4 py-3 flex items-center gap-3 min-w-[300px]">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Check className="w-4 h-4 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{toastMessage}</p>
            </div>
            <button
              onClick={() => setShowToast(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Professional Share Modal - Design Principles Applied */}
      {sharing.url && (
        <div
          data-testid="share-modal-backdrop"
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        >
          <div
            data-testid="share-modal"
            className="bg-warm-bg rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in duration-200"
          >
            {/* Header - Simplified (Principle #8: Contrast & Emphasis) */}
            <div className="px-8 pt-8 pb-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <Share className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Link Created!</h3>
                    <p className="text-sm text-gray-600 mt-0.5">Share your interactive lens</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSharing(prev => ({ ...prev, url: '' }));
                    setCopied(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content - URL is the star (Principle #1: Visual Hierarchy) */}
            <div className="px-8 pb-8">
              <div className="mb-6">
                <label className="text-sm font-semibold text-gray-900 mb-3 block">
                  Your shareable link
                </label>
                {/* URL Display - Large, prominent (Principle #5: Readability) */}
                <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4 mb-3">
                  <input
                    data-testid="share-input-url"
                    type="text"
                    value={sharing.url}
                    readOnly
                    aria-label="Shareable URL for this syntax comparison"
                    className="w-full bg-transparent text-base text-gray-900 font-mono outline-none"
                  />
                </div>

                {/* Copy Button - Clear affordance (Principle #7: Affordance) */}
                <button
                  data-testid="share-button-copy"
                  data-state={copied ? 'copied' : 'idle'}
                  aria-label="Copy shareable URL to clipboard"
                  onClick={() => {
                    navigator.clipboard?.writeText(sharing.url).catch(() => {
                      const textArea = document.createElement('textarea');
                      textArea.value = sharing.url;
                      textArea.style.position = 'fixed';
                      textArea.style.opacity = '0';
                      document.body.appendChild(textArea);
                      textArea.select();
                      document.execCommand('copy');
                      textArea.remove();
                    });
                    // Feedback (Principle #13: Feedback Loop)
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 hover:bg-orange-600 font-medium rounded-xl transition-all duration-200 hover:shadow-lg border-2 border-orange-500 text-orange-600 hover:text-white"
                >
                  {copied ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy Link
                    </>
                  )}
                </button>
              </div>

              {/* Info - Low contrast, supporting role (Principle #9: White Space) */}
              <div className="flex items-start gap-3 text-sm text-gray-600 bg-gray-50 rounded-lg p-4">
                <span className="text-orange-500 text-lg">ℹ️</span>
                <p className="leading-relaxed">
                  Anyone with this link can view your interactive code translation. The link remains active indefinitely.
                </p>
              </div>
            </div>

            {/* Footer - Secondary actions (Principle #1: Visual Hierarchy) */}
            <div className="bg-gray-50 px-8 py-4 flex items-center justify-between border-t border-gray-200">
              <button
                onClick={() => window.open(sharing.url, '_blank')}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Open in New Tab
              </button>
              <button
                onClick={() => {
                  setSharing(prev => ({ ...prev, url: '' }));
                  setCopied(false);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Toast */}
      {sharing.error && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top duration-200">
          <div className="bg-white rounded-lg shadow-2xl border border-red-200 max-w-md overflow-hidden">
            <div className="flex items-start gap-3 p-4">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <XCircle className="w-4 h-4 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-gray-900 mb-1">Sharing Failed</h4>
                <p className="text-sm text-gray-600">{sharing.error}</p>
              </div>
              <button
                onClick={() => setSharing(prev => ({ ...prev, error: '' }))}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Language Mismatch Modal */}
      {showMismatchModal && languageMismatch && (
        <LanguageMismatchModal
          detectedLanguage={languageMismatch.detected}
          sourceLanguage={sourceLanguage}
          targetLanguage={targetLanguage}
          confidence={languageMismatch.rawRelevance}
          onKeep={() => {
            setShowMismatchModal(false)
            performGeneration()
          }}
          onCancel={handleCancelGeneration}
        />
      )}

      {/* Debug Panel */}
      <ZenSimpleDebugPanel
        isOpen={isDebugOpen}
        onClose={() => setIsDebugOpen(false)}
        apiCalls={apiCalls}
        onClear={() => setApiCalls([])}
      />

      {/* Enhanced Syntax Highlighting Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .keyword { color: #ff6b6b !important; font-weight: 600; }
          .type-keyword { color: #4ecdc4 !important; font-weight: 500; }
          .function, .method { color: #45b7d1 !important; font-weight: 500; }
          .variable { color: #ecf0f1 !important; }
          .operator { color: #a8e6cf !important; font-weight: 500; }
          .string { color: #feca57 !important; }
          .number { color: #ff9ff3 !important; }
          .comment { color: #95a5a6 !important; font-style: italic; }
          .bracket { color: #ffa726 !important; font-weight: 600; }
          
          .hoverable {
            display: inline-block;
            transition: all 0.2s ease;
            cursor: pointer;
            border-radius: 3px;
            padding: 1px 2px;
          }
          
          .hoverable:hover {
            background: rgba(59, 130, 246, 0.4) !important;
            filter: brightness(1.3);
            box-shadow: 0 0 8px rgba(59, 130, 246, 0.5);
            transform: translateY(-1px);
          }
        `
      }} />

      {/* Admin Settings Panel */}
      <AdminSettingsPanel
        isOpen={isAdminPanelOpen}
        onClose={() => setIsAdminPanelOpen(false)}
        streamingEnabled={streamingEnabled}
        onStreamingToggle={handleStreamingToggle}
        promptVersion={promptVersion}
        isPromptSelectorOpen={isPromptSelectorOpen}
        onPromptVersionChange={setPromptVersion}
        onPromptSelectorToggle={() => setIsPromptSelectorOpen(!isPromptSelectorOpen)}
        providers={providers}
        currentProvider={currentProvider}
        switchProvider={switchProvider}
        isLoadingProviders={isLoadingProviders}
        enableLanguagePairPrompts={enableLanguagePairPrompts}
        onLanguagePairPromptsToggle={(enabled) => {
          console.log('🔄 [ZenCreator] Updating enableLanguagePairPrompts to:', enabled)
          updateSetting('enableLanguagePairPrompts', enabled)
        }}
      />
    </div>
  )
}

export default ZenCreatorSimple